// src/components/Spreadsheet/GroupedSpreadsheetSheet.jsx

import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import GroupedSpreadsheetRow from './GroupedSpreadsheetRow';

import {
  SPREADSHEET_SORT_DIRECTIONS,
  getColumnKey,
  getColumnWidth,
  getColumnWidthKey,
  getGroupId,
  getRowId,
  getVisibleColumns,
  isSpreadsheetColumnSortable,
  normalizeString,
  toArraySafe,
} from './groupedSpreadsheetUtils';

/**
 * GroupedSpreadsheetSheet
 * -----------------------------------------------------------------------------
 * Renders one unified grouped spreadsheet surface.
 *
 * PURPOSE
 *  • Render one coherent sheet with:
 *      - one sticky row-number lane
 *      - one grouped field-header row
 *      - one column-header row
 *      - row-driven body across all groups
 *  • Keep rendering domain-agnostic for both Form Lists and SmartLists
 *  • Delegate cell behavior to GroupedSpreadsheetRow/Cell
 *  • Support future virtualization by keeping row identity separate from DOM
 *
 * VIRTUALIZATION-READY DESIGN
 *  • Does not own row state
 *  • Does not derive data from mounted DOM
 *  • Accepts stable groups, rows, and column config from parent/canvas
 *  • Uses groupId + rowId + columnKey addressing
 *  • Keeps row numbers as visual chrome only
 *  • Allows future replacement of the row loop with a virtualized row window
 *
 * ENTERPRISE SPREADSHEET FOCUS MODEL
 *  • The canvas owns keyboard focus in view mode.
 *  • Cells are selectable visual surfaces.
 *  • Editing mode is controlled by the canvas and passed down as editingCell.
 *  • This sheet only routes props; it does not focus cells directly.
 *
 * IMPORTANT
 *  • This component does not know about:
 *      - dropdownOptions
 *      - dropdownDependencies
 *      - managedFields
 *      - Firestore
 *      - save/publish
 *
 * WHY THIS VERSION EXISTS
 *  • The earlier shared-engine version rendered one sheet per group.
 *  • That caused each dropdown/SmartList group to show its own row-number
 *    lane, making the UI look like multiple spreadsheets.
 *  • This version follows the SmartLists architecture: one sheet, one row
 *    number lane, many grouped column blocks.
 * -----------------------------------------------------------------------------
 */

const DEFAULT_ROW_HEIGHT = 24;
const DEFAULT_ROW_NUMBER_WIDTH = 40;
const DEFAULT_GROUP_HEADER_HEIGHT = 32;
const DEFAULT_COLUMN_HEADER_HEIGHT = 26;

/**
 * Frozen pane layering
 * -----------------------------------------------------------------------------
 * Google-Sheets-like spreadsheets use opaque frozen panes above the scrolling
 * grid body.
 *
 * WHY
 *  • Body cells are allowed to scroll underneath sticky row/column headers.
 *  • Sticky panes must therefore paint opaque backgrounds and sit in a clear
 *    z-index hierarchy.
 *  • This prevents body-cell text from bleeding through row numbers or headers.
 *
 * LAYER ORDER
 *  • body cells                 -> base layer
 *  • sticky row-number rail     -> above body cells
 *  • sticky column header row   -> above body cells and row numbers
 *  • sticky group header row    -> above column headers
 *  • sticky top-left corners    -> highest pane intersections
 */
const FROZEN_PANE_Z_INDEX = Object.freeze({
  rowNumber: 20,
  columnHeader: 30,
  groupHeader: 40,
  corner: 50,
});

const getGroupTitle = (group = {}) => {
  return (
    normalizeString(group?.title, '') ||
    normalizeString(group?.field?.label, '') ||
    normalizeString(group?.label, '') ||
    'Untitled group'
  );
};

const getRowNumberLabel = (rowIndex) => String(rowIndex + 1);

const areSetsEqual = (a, b) => {
  if (a === b) return true;

  if (!(a instanceof Set) || !(b instanceof Set)) {
    return false;
  }

  if (a.size !== b.size) return false;

  for (const value of a) {
    if (!b.has(value)) return false;
  }

  return true;
};

const areDirtyCellsByGroupEqual = (a, b) => {
  if (a === b) return true;

  const aGroupKeys = Object.keys(a || {});
  const bGroupKeys = Object.keys(b || {});

  if (aGroupKeys.length !== bGroupKeys.length) return false;

  return aGroupKeys.every((groupId) => {
    const aRows = a?.[groupId] || {};
    const bRows = b?.[groupId] || {};

    const aRowKeys = Object.keys(aRows);
    const bRowKeys = Object.keys(bRows);

    if (aRowKeys.length !== bRowKeys.length) return false;

    return aRowKeys.every((rowId) => (
      areSetsEqual(aRows?.[rowId], bRows?.[rowId])
    ));
  });
};

const areColumnWidthsEqualForGroups = ({
  groups,
  columns,
  prevWidths,
  nextWidths,
}) => {
  if (prevWidths === nextWidths) return true;

  const allColumnWidthKeys = new Set();

  toArraySafe(groups).forEach((group) => {
    const groupId = getGroupId(group);

    const groupColumns =
      Array.isArray(group?.columns) && group.columns.length > 0
        ? group.columns
        : columns;

    getVisibleColumns(groupColumns).forEach((column) => {
      const columnKey = getColumnKey(column);
      if (!columnKey) return;

      allColumnWidthKeys.add(columnKey);
      allColumnWidthKeys.add(getColumnWidthKey({
        groupId,
        column,
        columnKey,
      }));
    });
  });

  return Array.from(allColumnWidthKeys).every((key) => (
    prevWidths?.[key] === nextWidths?.[key]
  ));
};

const isSelectedCellInSheet = ({
  selectedCell,
  groups = [],
  rowKey = 'rows',
  rowIdKey = 'rowId',
}) => {
  if (!selectedCell) return false;

  return toArraySafe(groups).some((group) => {
    if (getGroupId(group) !== normalizeString(selectedCell?.groupId, '')) {
      return false;
    }

    return toArraySafe(group?.[rowKey]).some((row) => (
      getRowId(row, rowIdKey) === normalizeString(selectedCell?.rowId, '')
    ));
  });
};

const isEditingCellInSheet = ({
  editingCell,
  groups = [],
  rowKey = 'rows',
  rowIdKey = 'rowId',
}) => {
  if (!editingCell) return false;

  return toArraySafe(groups).some((group) => {
    if (getGroupId(group) !== normalizeString(editingCell?.groupId, '')) {
      return false;
    }

    return toArraySafe(group?.[rowKey]).some((row) => (
      getRowId(row, rowIdKey) === normalizeString(editingCell?.rowId, '')
    ));
  });
};

const getGroupColumns = ({
  group,
  columns,
}) => {
  return Array.isArray(group?.columns) && group.columns.length > 0
    ? group.columns
    : columns;
};

const getGroupContentWidth = ({
  group,
  columns,
  columnWidths,
}) => {
  const groupId = getGroupId(group);

  const groupColumns = getGroupColumns({
    group,
    columns,
  });

  return getVisibleColumns(groupColumns).reduce((sum, column) => {
    return sum + getColumnWidth({
      groupId,
      column,
      columnWidths,
      fallback: 100,
    });
  }, 0);
};

const getTotalSheetWidth = ({
  groups,
  columns,
  columnWidths,
  rowNumberWidth,
}) => {
  const groupedWidth = toArraySafe(groups).reduce((sum, group) => {
    return sum + getGroupContentWidth({
      group,
      columns,
      columnWidths,
    });
  }, 0);

  return rowNumberWidth + groupedWidth;
};

const getMaxRowCount = ({
  groups,
  rowKey,
  rowCount,
}) => {
  const explicitRowCount = Number(rowCount);

  if (Number.isFinite(explicitRowCount) && explicitRowCount > 0) {
    return explicitRowCount;
  }

  return toArraySafe(groups).reduce((max, group) => {
    return Math.max(max, toArraySafe(group?.[rowKey]).length);
  }, 0);
};

const isSelectedVisualRow = ({
  groups,
  rowIndex,
  selectedCell,
  rowKey,
  rowIdKey,
}) => {
  if (!selectedCell) return false;

  return toArraySafe(groups).some((group) => {
    const groupId = getGroupId(group);

    if (groupId !== normalizeString(selectedCell?.groupId, '')) {
      return false;
    }

    const row = toArraySafe(group?.[rowKey])[rowIndex];

    if (!row) return false;

    return getRowId(row, rowIdKey) === normalizeString(selectedCell?.rowId, '');
  });
};

const GroupedSpreadsheetSheet = memo(({
  groups = [],
  columns,
  rowCount = 0,
  rowKey = 'rows',
  columnWidths = {},
  selectedCell = null,
  editingCell = null,
  dirtyCellsByGroup = {},
  showDirtyCellIndicator = true,
  sortState = null,
  onSortColumn,
  isColumnSortDisabled,
  rowHeight = DEFAULT_ROW_HEIGHT,
  rowNumberWidth = DEFAULT_ROW_NUMBER_WIDTH,
  groupHeaderHeight = DEFAULT_GROUP_HEADER_HEIGHT,
  columnHeaderHeight = DEFAULT_COLUMN_HEADER_HEIGHT,
  rowIdKey = 'rowId',
  readOnly = false,
  disabled = false,
  sx = null,
  registerCellRef,
  onSelectCell,
  onCommitCellValue,
  onRequestCellEdit,
  onEndCellEdit,
  onEditInputReady,
  onKeyDown,
  onPaste,
  renderCustomCell,
  renderGroupHeaderContent,
  renderGroupActions,
  isCellReadOnly,
  isCellDisabled,
  isCellDirty,
  onStartColumnResize,
  onAutoFitColumn,
}) => {
  const safeGroups = useMemo(
    () => toArraySafe(groups),
    [groups],
  );

  const safeRowCount = useMemo(() => getMaxRowCount({
    groups: safeGroups,
    rowKey,
    rowCount,
  }), [
    safeGroups,
    rowKey,
    rowCount,
  ]);

  const totalWidth = useMemo(() => getTotalSheetWidth({
    groups: safeGroups,
    columns,
    columnWidths,
    rowNumberWidth,
  }), [
    safeGroups,
    columns,
    columnWidths,
    rowNumberWidth,
  ]);

  const sheetSx = useMemo(() => ({
    display: 'flex',
    flexDirection: 'column',
    verticalAlign: 'top',
    position: 'relative',

    /**
     * Local stacking context
     * -------------------------------------------------------------------------
     * Keeps frozen-pane z-index rules scoped to this spreadsheet surface.
     * This prevents sticky header/row-number layers from unexpectedly competing
     * with app-level drawers, side panels, dialogs, or nav chrome.
     */
    isolation: 'isolate',

    width: totalWidth,
    minWidth: totalWidth,
    maxWidth: totalWidth,
    borderTop: '1px solid',
    borderLeft: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    ...(typeof sx === 'object' && !Array.isArray(sx) ? sx : {}),
  }), [totalWidth, sx]);

  if (safeGroups.length === 0) {
    return null;
  }

  return (
    <Box
      role="grid"
      aria-label="Grouped spreadsheet"
      sx={sheetSx}
    >
      <Box
        role="row"
        sx={{
          display: 'flex',
          position: 'sticky',
          top: 0,
          zIndex: FROZEN_PANE_Z_INDEX.groupHeader,
          height: groupHeaderHeight,
          minHeight: groupHeaderHeight,
          width: totalWidth,
          backgroundColor: 'grey.100',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxSizing: 'border-box',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: FROZEN_PANE_Z_INDEX.corner,
            width: rowNumberWidth,
            minWidth: rowNumberWidth,
            maxWidth: rowNumberWidth,
            height: '100%',
            borderRight: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.100',
            boxSizing: 'border-box',
            flexShrink: 0,
            boxShadow: '1px 0 0 rgba(0, 0, 0, 0.08)',
          }}
        />

        {safeGroups.map((group) => {
          const groupId = getGroupId(group);
          const groupTitle = getGroupTitle(group);
          const groupColumns = getGroupColumns({
            group,
            columns,
          });

          const contentWidth = getGroupContentWidth({
            group,
            columns,
            columnWidths,
          });

          if (!groupId) return null;

          const renderedGroupHeaderContent =
            typeof renderGroupHeaderContent === 'function'
              ? renderGroupHeaderContent({
                  group,
                  groupId,
                  title: groupTitle,
                  readOnly,
                  disabled,
                })
              : null;

          return (
            <Box
              key={`${groupId}-group-header`}
              role="columnheader"
              aria-colspan={getVisibleColumns(groupColumns).length}
              sx={{
                width: contentWidth,
                minWidth: contentWidth,
                maxWidth: contentWidth,
                height: '100%',
                px: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                overflow: 'hidden',
                borderRight: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.100',
                boxSizing: 'border-box',
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'text.primary',
                }}
              >
                {renderedGroupHeaderContent || (
                  <Tooltip title={groupTitle} placement="top">
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        fontWeight: 700,
                        minWidth: 0,
                        color: 'inherit',
                      }}
                    >
                      {groupTitle}
                    </Typography>
                  </Tooltip>
                )}
              </Box>

              {typeof renderGroupActions === 'function' ? (
                <Box
                  sx={{
                    ml: 1,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  {renderGroupActions({
                    group,
                    groupId,
                    readOnly,
                    disabled,
                  })}
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>

      <Box
        role="row"
        sx={{
          display: 'flex',
          position: 'sticky',
          top: groupHeaderHeight,
          zIndex: FROZEN_PANE_Z_INDEX.columnHeader,
          height: columnHeaderHeight,
          minHeight: columnHeaderHeight,
          width: totalWidth,
          backgroundColor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxSizing: 'border-box',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: FROZEN_PANE_Z_INDEX.corner,
            width: rowNumberWidth,
            minWidth: rowNumberWidth,
            maxWidth: rowNumberWidth,
            height: '100%',
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'grey.50',
            boxSizing: 'border-box',
            flexShrink: 0,
            boxShadow: '1px 0 0 rgba(0, 0, 0, 0.08)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            #
          </Typography>
        </Box>

        {safeGroups.map((group) => {
          const groupId = getGroupId(group);
          const groupColumns = getGroupColumns({
            group,
            columns,
          });

          if (!groupId) return null;

          return getVisibleColumns(groupColumns).map((column) => {
            const columnKey = getColumnKey(column);
            const width = getColumnWidth({
              groupId,
              column,
              columnWidths,
              fallback: 100,
            });

            if (!columnKey) return null;

            const canResize = column.resizable !== false;
            const canAutoFit = column.autoFit !== false;

            const sortDisabled =
              readOnly === true ||
              disabled === true ||
              group?.readOnly === true ||
              group?.disabled === true ||
              (
                typeof isColumnSortDisabled === 'function' &&
                isColumnSortDisabled({
                  group,
                  groupId,
                  column,
                  columnKey,
                }) === true
              );

            const isSortable =
              isSpreadsheetColumnSortable(column) &&
              typeof onSortColumn === 'function' &&
              sortDisabled !== true;

            const isSorted =
              normalizeString(sortState?.groupId, '') === groupId &&
              normalizeString(sortState?.columnKey, '') === columnKey;

            const sortDirection = isSorted
              ? normalizeString(sortState?.direction, '')
              : '';

            const ariaSort =
              isSorted && sortDirection === SPREADSHEET_SORT_DIRECTIONS.ASCENDING
                ? 'ascending'
                : isSorted &&
                    sortDirection === SPREADSHEET_SORT_DIRECTIONS.DESCENDING
                  ? 'descending'
                  : 'none';

            return (
              <Box
                key={`${groupId}-${columnKey}-header`}
                role="columnheader"
                aria-sort={isSortable ? ariaSort : undefined}
                data-group-id={groupId}
                data-column-key={columnKey}
                onClick={(event) => {
                  if (!isSortable) return;

                  event.preventDefault();

                  onSortColumn({
                    event,
                    group,
                    groupId,
                    column,
                    columnKey,
                  });
                }}
                sx={{
                  width,
                  minWidth: width,
                  maxWidth: width,
                  height: '100%',
                  px: 0.75,
                  boxSizing: 'border-box',
                  borderRight: '1px solid',
                  borderColor: isSorted ? 'primary.main' : 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isSorted
                    ? 'rgba(25, 118, 210, 0.08)'
                    : 'grey.50',
                  position: 'relative',
                  userSelect: 'none',
                  overflow: 'hidden',
                  flexShrink: 0,
                  cursor: isSortable ? 'pointer' : 'default',
                  transition: 'background-color 120ms ease, border-color 120ms ease',
                  '&:hover': isSortable
                    ? {
                        backgroundColor: isSorted
                          ? 'rgba(25, 118, 210, 0.12)'
                          : 'rgba(0, 0, 0, 0.04)',
                      }
                    : {},
                }}
              >
                <Tooltip
                  title={
                    isSortable
                      ? `${column?.headerTooltip || column?.label || columnKey}: click to sort`
                      : column?.headerTooltip || column?.label || columnKey
                  }
                  placement="top"
                >
                  <Box
                    sx={{
                      minWidth: 0,
                      maxWidth: '100%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.25,
                      color: isSorted ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'inherit',
                        maxWidth: '100%',
                      }}
                    >
                      {column?.label || columnKey}
                    </Typography>

                    {isSorted ? (
                      sortDirection === SPREADSHEET_SORT_DIRECTIONS.ASCENDING ? (
                        <ArrowDropUpIcon
                          aria-hidden="true"
                          sx={{
                            fontSize: 16,
                            color: 'inherit',
                            flexShrink: 0,
                            ml: -0.25,
                          }}
                        />
                      ) : (
                        <ArrowDropDownIcon
                          aria-hidden="true"
                          sx={{
                            fontSize: 16,
                            color: 'inherit',
                            flexShrink: 0,
                            ml: -0.25,
                          }}
                        />
                      )
                    ) : null}
                  </Box>
                </Tooltip>

                {canResize && typeof onStartColumnResize === 'function' ? (
                  <Box
                    role="separator"
                    aria-orientation="vertical"
                    tabIndex={-1}
                    onMouseDown={(event) => {
                      /**
                       * Resize ownership
                       * -----------------------------------------------------------------------
                       * The separator is an interaction handle, not a normal column-header click.
                       *
                       * WHY
                       *  • Prevents the resize gesture from bubbling into header-level handlers.
                       *  • Avoids accidental selection/double-click/autofit behavior while the
                       *    user is dragging a boundary.
                       *  • Keeps resizing behavior aligned with local spreadsheet tools:
                       *      - drag separator = resize
                       *      - double-click separator = autofit
                       */
                      event.preventDefault();
                      event.stopPropagation();

                      onStartColumnResize({
                        event,
                        group,
                        groupId,
                        column,
                        columnKey,
                        startWidth: width,
                      });
                    }}
                    onDoubleClick={(event) => {
                      /**
                       * Separator autofit
                       * -----------------------------------------------------------------------
                       * Google-Sheets-like behavior: double-clicking the column boundary
                       * autofits that specific visual column.
                       *
                       * IMPORTANT
                       *  • Autofit is intentionally scoped to the separator, not the whole
                       *    column header.
                       *  • This prevents accidental snapback when users interact with the
                       *    header label or complete a resize gesture.
                       */
                      event.preventDefault();
                      event.stopPropagation();

                      if (!canAutoFit || !onAutoFitColumn) return;

                      onAutoFitColumn({
                        event,
                        group,
                        groupId,
                        column,
                        columnKey,
                      });
                    }}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: -3,
                      width: 6,
                      height: '100%',
                      cursor: 'col-resize',
                      zIndex: 2,
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        opacity: 0.35,
                      },
                    }}
                  />
                ) : null}
              </Box>
            );
          });
        })}
      </Box>

      <Box role="rowgroup">
        {Array.from({ length: safeRowCount }).map((_, rowIndex) => {
          const visualRowSelected = isSelectedVisualRow({
            groups: safeGroups,
            rowIndex,
            selectedCell,
            rowKey,
            rowIdKey,
          });

          return (
            <Box
              key={`grouped-spreadsheet-row-${rowIndex}`}
              role="row"
              sx={{
                display: 'flex',
                width: totalWidth,
                minHeight: rowHeight,
                height: rowHeight,
              }}
            >
              <Box
                aria-hidden="true"
                data-row-index={rowIndex}
                sx={(theme) => ({
                  position: 'sticky',
                  left: 0,
                  zIndex: FROZEN_PANE_Z_INDEX.rowNumber,
                  width: rowNumberWidth,
                  minWidth: rowNumberWidth,
                  maxWidth: rowNumberWidth,
                  height: rowHeight,
                  minHeight: rowHeight,
                  borderRight: '1px solid',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  boxSizing: 'border-box',

                  /**
                   * Opaque frozen row-number rail
                   * -----------------------------------------------------------------
                   * Body cells horizontally scroll underneath this sticky rail.
                   * The rail must therefore always paint an opaque base surface.
                   *
                   * WHY
                   *  • MUI action.selected is translucent.
                   *  • Using it directly as backgroundColor allows scrolled body
                   *    text to show through the row-number area.
                   *
                   * GOOGLE-SHEETS-LIKE RULE
                   *  • Frozen panes are opaque surfaces.
                   *  • Selection is rendered as an overlay/affordance on top of
                   *    the opaque surface, not as a transparent pane background.
                   */
                  backgroundColor: theme.palette.grey[50],
                  backgroundImage: visualRowSelected
                    ? `linear-gradient(${alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity || 0.08)}, ${alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity || 0.08)})`
                    : 'none',
                  backgroundClip: 'padding-box',
                  boxShadow: '1px 0 0 rgba(0, 0, 0, 0.08)',
                  overflow: 'hidden',

                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                })}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 11,
                    color: 'text.secondary',
                    userSelect: 'none',
                  }}
                >
                  {getRowNumberLabel(rowIndex)}
                </Typography>
              </Box>

              {safeGroups.map((group) => {
                const groupId = getGroupId(group);
                const groupColumns = getGroupColumns({
                  group,
                  columns,
                });

                if (!groupId) return null;

                const groupRows = toArraySafe(group?.[rowKey]);
                const row = groupRows[rowIndex];
                const groupReadOnly = readOnly || group?.readOnly === true;
                const groupDisabled = disabled || group?.disabled === true;

                const contentWidth = getGroupContentWidth({
                  group,
                  columns,
                  columnWidths,
                });

                if (!row) {
                  return (
                    <Box
                      key={`${groupId}-empty-row-${rowIndex}`}
                      aria-hidden="true"
                      sx={{
                        width: contentWidth,
                        minWidth: contentWidth,
                        maxWidth: contentWidth,
                        height: rowHeight,
                        borderBottom: '1px solid',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                      }}
                    />
                  );
                }

                const rowId = getRowId(row, rowIdKey);
                const dirtyCellKeys =
                  dirtyCellsByGroup?.[groupId]?.[rowId] || null;

                const isEditingRow =
                  editingCell &&
                  editingCell.groupId === groupId &&
                  editingCell.rowId === rowId;

                return (
                  <GroupedSpreadsheetRow
                    key={`${groupId}-${rowId}`}
                    group={group}
                    groupId={groupId}
                    row={row}
                    rowIndex={rowIndex}
                    columns={getVisibleColumns(groupColumns)}
                    columnWidths={columnWidths}
                    selectedCell={selectedCell}
                    editingCell={isEditingRow ? editingCell : null}
                    dirtyCellKeys={dirtyCellKeys}
                    showDirtyCellIndicator={showDirtyCellIndicator}
                    rowHeight={rowHeight}
                    readOnly={groupReadOnly}
                    disabled={groupDisabled}
                    rowReadOnly={row?.readOnly === true}
                    rowDisabled={row?.disabled === true}
                    rowIdKey={rowIdKey}
                    registerCellRef={registerCellRef}
                    onSelectCell={onSelectCell}
                    onCommitCellValue={onCommitCellValue}
                    onRequestCellEdit={onRequestCellEdit}
                    onEndCellEdit={onEndCellEdit}
                    onEditInputReady={onEditInputReady}
                    onKeyDown={onKeyDown}
                    onPaste={onPaste}
                    renderCustomCell={renderCustomCell}
                    isCellReadOnly={isCellReadOnly}
                    isCellDisabled={isCellDisabled}
                    isCellDirty={isCellDirty}
                  />
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}, (prev, next) => {
  if (prev.groups !== next.groups) return false;
  if (prev.columns !== next.columns) return false;
  if (prev.rowCount !== next.rowCount) return false;
  if (prev.rowKey !== next.rowKey) return false;
  if (prev.showDirtyCellIndicator !== next.showDirtyCellIndicator) return false;
  if (prev.sortState !== next.sortState) return false;
  if (prev.rowHeight !== next.rowHeight) return false;
  if (prev.rowNumberWidth !== next.rowNumberWidth) return false;
  if (prev.groupHeaderHeight !== next.groupHeaderHeight) return false;
  if (prev.columnHeaderHeight !== next.columnHeaderHeight) return false;
  if (prev.rowIdKey !== next.rowIdKey) return false;
  if (prev.readOnly !== next.readOnly) return false;
  if (prev.disabled !== next.disabled) return false;
  if (prev.sx !== next.sx) return false;

  if (prev.registerCellRef !== next.registerCellRef) return false;
  if (prev.onSelectCell !== next.onSelectCell) return false;
  if (prev.onCommitCellValue !== next.onCommitCellValue) return false;
  if (prev.onRequestCellEdit !== next.onRequestCellEdit) return false;
  if (prev.onEndCellEdit !== next.onEndCellEdit) return false;
  if (prev.onEditInputReady !== next.onEditInputReady) return false;
  if (prev.onKeyDown !== next.onKeyDown) return false;
  if (prev.onPaste !== next.onPaste) return false;
  if (prev.renderCustomCell !== next.renderCustomCell) return false;
  if (prev.renderGroupHeaderContent !== next.renderGroupHeaderContent) return false;
  if (prev.renderGroupActions !== next.renderGroupActions) return false;
  if (prev.isCellReadOnly !== next.isCellReadOnly) return false;
  if (prev.isCellDisabled !== next.isCellDisabled) return false;
  if (prev.isCellDirty !== next.isCellDirty) return false;
  if (prev.onStartColumnResize !== next.onStartColumnResize) return false;
  if (prev.onAutoFitColumn !== next.onAutoFitColumn) return false;
  if (prev.onSortColumn !== next.onSortColumn) return false;
  if (prev.isColumnSortDisabled !== next.isColumnSortDisabled) return false;

  if (!areDirtyCellsByGroupEqual(
    prev.dirtyCellsByGroup,
    next.dirtyCellsByGroup,
  )) {
    return false;
  }

  if (!areColumnWidthsEqualForGroups({
    groups: next.groups,
    columns: next.columns,
    prevWidths: prev.columnWidths,
    nextWidths: next.columnWidths,
  })) {
    return false;
  }

  const prevSelectedInSheet = isSelectedCellInSheet({
    selectedCell: prev.selectedCell,
    groups: prev.groups,
    rowKey: prev.rowKey,
    rowIdKey: prev.rowIdKey,
  });

  const nextSelectedInSheet = isSelectedCellInSheet({
    selectedCell: next.selectedCell,
    groups: next.groups,
    rowKey: next.rowKey,
    rowIdKey: next.rowIdKey,
  });

  if (prevSelectedInSheet !== nextSelectedInSheet) {
    return false;
  }

  if (prevSelectedInSheet && nextSelectedInSheet) {
    const prevGroupId = normalizeString(prev.selectedCell?.groupId, '');
    const nextGroupId = normalizeString(next.selectedCell?.groupId, '');
    const prevRowId = normalizeString(prev.selectedCell?.rowId, '');
    const nextRowId = normalizeString(next.selectedCell?.rowId, '');
    const prevColumnKey = normalizeString(prev.selectedCell?.columnKey, '');
    const nextColumnKey = normalizeString(next.selectedCell?.columnKey, '');

    if (prevGroupId !== nextGroupId) return false;
    if (prevRowId !== nextRowId) return false;
    if (prevColumnKey !== nextColumnKey) return false;
  }

  const prevEditingInSheet = isEditingCellInSheet({
    editingCell: prev.editingCell,
    groups: prev.groups,
    rowKey: prev.rowKey,
    rowIdKey: prev.rowIdKey,
  });

  const nextEditingInSheet = isEditingCellInSheet({
    editingCell: next.editingCell,
    groups: next.groups,
    rowKey: next.rowKey,
    rowIdKey: next.rowIdKey,
  });

  if (prevEditingInSheet !== nextEditingInSheet) {
    return false;
  }

  if (prevEditingInSheet && nextEditingInSheet) {
    const prevGroupId = normalizeString(prev.editingCell?.groupId, '');
    const nextGroupId = normalizeString(next.editingCell?.groupId, '');
    const prevRowId = normalizeString(prev.editingCell?.rowId, '');
    const nextRowId = normalizeString(next.editingCell?.rowId, '');
    const prevColumnKey = normalizeString(prev.editingCell?.columnKey, '');
    const nextColumnKey = normalizeString(next.editingCell?.columnKey, '');
    const prevInitialValue = prev.editingCell?.initialValue;
    const nextInitialValue = next.editingCell?.initialValue;

    if (prevGroupId !== nextGroupId) return false;
    if (prevRowId !== nextRowId) return false;
    if (prevColumnKey !== nextColumnKey) return false;
    if (prevInitialValue !== nextInitialValue) return false;
  }

  return true;
});

GroupedSpreadsheetSheet.displayName = 'GroupedSpreadsheetSheet';

GroupedSpreadsheetSheet.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  rowCount: PropTypes.number,
  rowKey: PropTypes.string,
  columnWidths: PropTypes.object,
  selectedCell: PropTypes.shape({
    groupId: PropTypes.string,
    rowId: PropTypes.string,
    columnKey: PropTypes.string,
  }),
  editingCell: PropTypes.shape({
    groupId: PropTypes.string,
    rowId: PropTypes.string,
    columnKey: PropTypes.string,
    initialValue: PropTypes.any,
  }),
  dirtyCellsByGroup: PropTypes.object,
  showDirtyCellIndicator: PropTypes.bool,
  sortState: PropTypes.shape({
    groupId: PropTypes.string,
    columnKey: PropTypes.string,
    direction: PropTypes.string,
  }),
  onSortColumn: PropTypes.func,
  isColumnSortDisabled: PropTypes.func,
  rowHeight: PropTypes.number,
  rowNumberWidth: PropTypes.number,
  groupHeaderHeight: PropTypes.number,
  columnHeaderHeight: PropTypes.number,
  rowIdKey: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
  registerCellRef: PropTypes.func,
  onSelectCell: PropTypes.func,
  onCommitCellValue: PropTypes.func,
  onRequestCellEdit: PropTypes.func,
  onEndCellEdit: PropTypes.func,
  onEditInputReady: PropTypes.func,
  onKeyDown: PropTypes.func,
  onPaste: PropTypes.func,
  renderCustomCell: PropTypes.func,
  renderGroupHeaderContent: PropTypes.func,
  renderGroupActions: PropTypes.func,
  isCellReadOnly: PropTypes.func,
  isCellDisabled: PropTypes.func,
  isCellDirty: PropTypes.func,
  onStartColumnResize: PropTypes.func,
  onAutoFitColumn: PropTypes.func,
};

export default GroupedSpreadsheetSheet;